import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import { Building2, Edit3, Save, X, Upload, Globe, Phone, FileText, Key, Copy } from 'lucide-react';

interface OrganizationProfile {
  id: string;
  organizationId: string;
  companyName: string;
  companyMotto?: string;
  companyWebsite?: string;
  companyPhone?: string;
  companyABN?: string;
  companyACN?: string;
  logoFileName?: string;
  logoUrl?: string;
  otherInfo?: string;
  joiningCode: string; // Added joining code field
  createdAt: string;
  updatedAt: string;
}

interface OrganizationStaff {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isHeadOfOrganization: boolean;
  isAdmin: boolean;
}

interface OrganizationProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
}

export default function OrganizationProfileModal({ 
  isOpen, 
  onClose, 
  organizationId 
}: OrganizationProfileModalProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState<Partial<OrganizationProfile>>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const queryClient = useQueryClient();

  // Fetch organization profile
  const { data: profile, isLoading: profileLoading } = useQuery<OrganizationProfile>({
    queryKey: ['organization-profile', organizationId],
    queryFn: () => api.get(`/organizations/profile/${organizationId}`).then(res => res.data.data),
    enabled: isOpen && !!organizationId,
  });

  // Fetch organization staff for head and admins
  const { data: staff, isLoading: staffLoading } = useQuery<OrganizationStaff[]>({
    queryKey: ['organization-staff-for-profile', organizationId],
    queryFn: () => api.get(`/organizations/staff?org=${organizationId}`).then(res => res.data.data),
    enabled: isOpen && !!organizationId,
  });

  // Check if current user can edit (head or admin)
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: () => api.get('/users/me').then(res => res.data.data), // Standardize response format
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<OrganizationProfile>) => 
      api.put(`/organizations/profile/${organizationId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-profile', organizationId] });
      setIsEditMode(false);
      setEditData({});
      setLogoFile(null);
    },
  });

  // Upload logo mutation
  const uploadLogoMutation = useMutation({
    mutationFn: (formData: FormData) => 
      api.post(`/organizations/profile/${organizationId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-profile', organizationId] });
    },
  });

  // Initialize edit data when entering edit mode
  useEffect(() => {
    if (isEditMode && profile) {
      setEditData({
        companyName: profile.companyName,
        companyMotto: profile.companyMotto || '',
        companyWebsite: profile.companyWebsite || '',
        companyPhone: profile.companyPhone || '',
        companyABN: profile.companyABN || '',
        companyACN: profile.companyACN || '',
        otherInfo: profile.otherInfo || '',
      });
    }
  }, [isEditMode, profile]);

  const handleSave = async () => {
    try {
      // Upload logo first if there's a new file
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        await uploadLogoMutation.mutateAsync(formData);
      }

      // Update profile data
      await updateProfileMutation.mutateAsync(editData);
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditData({});
    setLogoFile(null);
  };

  const handleCopyJoiningCode = async () => {
    if (profile?.joiningCode) {
      try {
        await navigator.clipboard.writeText(profile.joiningCode);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy joining code:', err);
      }
    }
  };

  if (!isOpen) return null;

  if (profileLoading || staffLoading) {
    return createPortal(
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-lg shadow-lg max-w-md w-full">
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  if (!profile) return null;

  // Find head of organization and admins
  const headOfOrg = staff?.find(s => s.isHeadOfOrganization);
  const admins = staff?.filter(s => s.isAdmin) || []; // Include all admins, even if they're also head
  
  // Check if current user can edit
  const canEdit = currentUser && staff?.some(s => {
    const userEmail = currentUser.email || currentUser.data?.email;
    return s.email === userEmail && (s.isHeadOfOrganization || s.isAdmin);
  });

  // Generate default logo (blue box with first letter)
  const defaultLogo = (
    <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center text-white text-2xl font-bold">
      {profile.companyName.charAt(0).toUpperCase()}
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5" />
            <h2 className="text-xl font-semibold">
              {isEditMode ? 'Edit Organization Profile' : 'Organization Profile'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
          {/* Company Logo and Name */}
          <div className="flex items-center gap-4">
            {isEditMode ? (
              <div className="space-y-2">
                {profile.logoUrl ? (
                  <img 
                    src={profile.logoUrl} 
                    alt="Company Logo" 
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  defaultLogo
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className="inline-flex items-center gap-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md cursor-pointer"
                  >
                    <Upload className="h-4 w-4" />
                    Change Logo
                  </label>
                </div>
              </div>
            ) : (
              profile.logoUrl ? (
                <img 
                  src={profile.logoUrl} 
                  alt="Company Logo" 
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                defaultLogo
              )
            )}
            
            <div className="flex-1">
              {isEditMode ? (
                <input
                  type="text"
                  value={editData.companyName || ''}
                  onChange={(e) => setEditData({ ...editData, companyName: e.target.value })}
                  className="w-full text-2xl font-bold bg-background border border-input rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Company Name"
                />
              ) : (
                <h2 className="text-2xl font-bold">{profile.companyName}</h2>
              )}
            </div>
          </div>

          {/* Company Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Motto */}
            <div>
              <label className="block text-sm font-medium mb-1">Company Motto</label>
              {isEditMode ? (
                <input
                  type="text"
                  value={editData.companyMotto || ''}
                  onChange={(e) => setEditData({ ...editData, companyMotto: e.target.value })}
                  className="w-full bg-background border border-input rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter company motto"
                />
              ) : (
                <p className="text-gray-700">{profile.companyMotto || 'Not specified'}</p>
              )}
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Website
              </label>
              {isEditMode ? (
                <input
                  type="text"
                  value={editData.companyWebsite || ''}
                  onChange={(e) => setEditData({ ...editData, companyWebsite: e.target.value })}
                  className="w-full bg-background border border-input rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="https://example.com"
                />
              ) : (
                profile.companyWebsite ? (
                  <a 
                    href={profile.companyWebsite} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {profile.companyWebsite}
                  </a>
                ) : (
                  <p className="text-gray-700">Not specified</p>
                )
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Phone
              </label>
              {isEditMode ? (
                <input
                  type="text"
                  value={editData.companyPhone || ''}
                  onChange={(e) => setEditData({ ...editData, companyPhone: e.target.value })}
                  className="w-full bg-background border border-input rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter phone number"
                />
              ) : (
                <p className="text-gray-700">{profile.companyPhone || 'Not specified'}</p>
              )}
            </div>

            {/* ABN */}
            <div>
              <label className="block text-sm font-medium mb-1">ABN</label>
              {isEditMode ? (
                <input
                  type="text"
                  value={editData.companyABN || ''}
                  onChange={(e) => setEditData({ ...editData, companyABN: e.target.value })}
                  className="w-full bg-background border border-input rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter ABN"
                />
              ) : (
                <p className="text-gray-700">{profile.companyABN || 'Not specified'}</p>
              )}
            </div>

            {/* ACN */}
            <div>
              <label className="block text-sm font-medium mb-1">ACN</label>
              {isEditMode ? (
                <input
                  type="text"
                  value={editData.companyACN || ''}
                  onChange={(e) => setEditData({ ...editData, companyACN: e.target.value })}
                  className="w-full bg-background border border-input rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter ACN"
                />
              ) : (
                <p className="text-gray-700">{profile.companyACN || 'Not specified'}</p>
              )}
            </div>
          </div>

          {/* Head of Organization */}
          <div>
            <label className="block text-sm font-medium mb-1">Head of Organization</label>
            {headOfOrg ? (
              <p className="text-gray-700">
                {headOfOrg.firstName} {headOfOrg.lastName} ({headOfOrg.email})
              </p>
            ) : (
              <p className="text-gray-500 italic">No head of organization assigned</p>
            )}
          </div>

          {/* Site Admins */}
          <div>
            <label className="block text-sm font-medium mb-1">Site Admins</label>
            {admins.length > 0 ? (
              <div className="space-y-1">
                {admins.map((admin) => (
                  <p key={admin.id} className="text-gray-700">
                    {admin.firstName} {admin.lastName} ({admin.email})
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">No site admins assigned</p>
            )}
          </div>

          {/* Organization Joining Code */}
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-2">
              <Key className="h-4 w-4" />
              Organization Joining Code
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-50 border border-input rounded-md px-3 py-2 font-mono text-lg tracking-wider">
                {profile.joiningCode}
              </div>
              <button
                onClick={handleCopyJoiningCode}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                title="Copy joining code"
              >
                <Copy className="h-4 w-4" />
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Share this code with new team members so they can join your organization
            </p>
          </div>

          {/* Other Information */}
          {(isEditMode || profile.otherInfo) && (
            <div>
              {isEditMode && (
                <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Other Information
                </label>
              )}
              {isEditMode ? (
                <textarea
                  value={editData.otherInfo || ''}
                  onChange={(e) => setEditData({ ...editData, otherInfo: e.target.value })}
                  className="w-full bg-background border border-input rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  placeholder="Enter any additional information"
                  rows={4}
                />
              ) : (
                profile.otherInfo && (
                  <div className="whitespace-pre-wrap text-gray-700 bg-gray-50 p-3 rounded-md">
                    {profile.otherInfo}
                  </div>
                )
              )}
            </div>
          )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <div>
            {canEdit && !isEditMode && (
              <button
                onClick={() => setIsEditMode(true)}
                className="flex items-center gap-2 px-4 py-2 border border-input rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Edit3 className="h-4 w-4" />
                Edit
              </button>
            )}
          </div>
          
          <div className="flex gap-2">
            {isEditMode ? (
              <>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 border border-input rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateProfileMutation.isLoading || uploadLogoMutation.isLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4" />
                  {(updateProfileMutation.isLoading || uploadLogoMutation.isLoading) ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <button 
                onClick={onClose}
                className="px-4 py-2 border border-input rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
